import { listAnimationPresets, resolvePresetParams } from 'adflow';

import {
  ANIMATION_DELIVERY_LIMITS,
  ANIMATION_RENDER_LIMITS,
  supportedVideoFormats,
  type IsTypeSupported,
} from '../../animationRender';
import { ANIMATION_OPERATIONS } from '../operations';
import { ANIMATION_CAPABILITY_FREEZE_DATE } from './capabilityMatrix';
import { ANIMATION_EASINGS, ANIMATION_PARAMETERS, parameterNamesFor } from './params';

export const ANIMATION_ROLE: Record<'IN' | 'OUT', 'entrance' | 'exit'> = {
  IN: 'entrance',
  OUT: 'exit',
};

export type AnimationCatalogEnvironment = {
  isTypeSupported: IsTypeSupported | null;
  artifactUrlAvailable: boolean;
};

export function animationPresetCatalog() {
  return listAnimationPresets().map((preset) => ({
    presetId: preset.id,
    label: preset.label,
    phase: preset.phase,
    role: ANIMATION_ROLE[preset.phase],
    group: preset.group,
    property: preset.property,
    defaults: resolvePresetParams(preset),
    parameters: parameterNamesFor(preset),
  }));
}

export function animationCapabilityDetails(environment: AnimationCatalogEnvironment) {
  const videoFormats = supportedVideoFormats(environment.isTypeSupported);
  return {
    enabled: true,
    capabilityFreeze: ANIMATION_CAPABILITY_FREEZE_DATE,
    operations: [...ANIMATION_OPERATIONS],
    units: { time: 'seconds', distance: 'pixels' },
    presets: animationPresetCatalog(),
    parameters: ANIMATION_PARAMETERS,
    easings: ANIMATION_EASINGS,
    targets: {
      elements: 'Any element id on the open Figma template.',
      properties: ['opacity', 'scale', 'x', 'y'],
    },
    rules: {
      duplicate: 'The same preset on the same target returns ANIMATION_DUPLICATE.',
      replace:
        'apply_animation_effect with the same target, phase, and property replaces the old effect and lists it in replaced.',
      coexist: 'Effects with a different phase or property coexist.',
      changePreset:
        'update_animation_effect presetId keeps the role. A duplicate or conflicting preset is rejected.',
      readOnly: 'Timeline duration, FPS, loop, tracks, and keyframes are read-only.',
    },
    previews: {
      modes: ['frame', 'contact_sheet', 'video'],
      frame: { mimeType: 'image/png', args: ['timeSeconds'] },
      contactSheet: {
        mimeType: 'image/png',
        defaultFrames: ANIMATION_RENDER_LIMITS.defaultContactSheetFrames,
        maxFrames: ANIMATION_RENDER_LIMITS.maxContactSheetFrames,
        maxEdgePx: ANIMATION_RENDER_LIMITS.maxContactSheetEdgePx,
      },
      video: {
        defaultFps: ANIMATION_RENDER_LIMITS.defaultVideoFps,
        maxFps: ANIMATION_RENDER_LIMITS.maxVideoFps,
        maxSeconds: ANIMATION_RENDER_LIMITS.maxVideoSeconds,
        formats: videoFormats.map(({ container, mimeType, codec }) => ({
          container,
          mimeType,
          codec,
        })),
        preferred: videoFormats[0]?.container ?? null,
        maxEmbeddedBytes: ANIMATION_DELIVERY_LIMITS.maxEmbeddedBytes,
        artifactUrl: environment.artifactUrlAvailable
          ? { available: true, ttlSeconds: ANIMATION_DELIVERY_LIMITS.artifactTtlSeconds }
          : { available: false },
      },
    },
  };
}
